// eslint-disable-next-line no-use-before-define
import React, { Fragment, useState, useEffect, useMemo } from 'react';

import { useRouteMatch, useHistory } from 'react-router-dom';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { ROAutocomplete, ROTextField } from 'shared-components/components/FormFields';
import ROPatientCarePlanPageFooter from '../Footer';
import OrgansAtRiskModule from './OrgansAtRisk/OrgansAtRisk';
import PrioritisedOrgansAtRiskModule from './OrgansAtRisk/PrioritisedOrgansAtRisk';
import { ROPatientCarePlanRoute } from '../Interface';
import SiteTargetValues from './SiteTargetValues';

import {
  GET_CAREPLAN_SITE_GROUPS,
  UPDATE_VOLUMING_VALUE,
  SITE_TEMPLATE_VALUES_BY_PRESCRIPTION_VARIATION,
  GET_CAREPLAN,
  GET_CAREPLAN_STATUS,
  TRIGGER_CPOT,
  GET_CAREPLAN_PRESCRIPTIONS,
  VOLUMING_TEMPLATE_VALUE_VARIATION,
} from '../Queries';
import { OAR_QUERY, ORGAN_AT_RISK_TEMPLATE_VALUE_VARIATION, VOLUMING_OAR_QUERY } from './OrgansAtRisk/Queries';
import { LIST_CAREPLAN_FILTERS, GET_TUMOUR_STREAM_FROM_CAREPLAN } from '../TemplatePage/TemplatePageQueries';
import { UPDATE_MANUAL_OARS, GET_VOLUMING_OAR_DATA, RESET_VOLUMING_DATA } from './Queries';
import { CareplanBanners } from '../Banner/Banner';
import { CAREPLAN_PAGES } from '../Constants';
import './VolumingPage.scss';
import { ADDITIONAL_OAR_INFO, SELECTED_TEMPLATE, OAR } from './Interface';
import ROPatientCarePathSidePanel from '../SidePanel/SidePanel';
import { removeUnicodeQuotes, getTreatmentSiteHeader, getPrescriptionsWithSites } from '../Common';
import { LoadingSpinner } from 'shared-components/components';
import CareplanContainer from '../Container';
import ModalCpotCancel from '../ModalCpotCancel/ModalCpotCancel';
import { CurrentAppConfig } from '../AppConfig';
import { useTenantAppConfig } from 'op-utils/useTenantAppConfig';
import { Logger } from 'shared-components/utils';
import { logMissingDiagnosisCode } from '../Common';
import { useTheme, Divider, FormLabel, Box, Typography } from '@mui/material';
import { SelectOptionType } from 'shared-components/components/FormFields';
import { TENANT_CONFIG_QUERY } from '../../../../components/RO/Base/TenantConfigQuery';
import { useCareplanDataContext } from '../CareplanContext/CareplanDataContext';

const logger = new Logger('VolumingPage.tsx');

interface careplanTemplateList {
  id: string;
  name: string;
  rule: {
    id: string;
    field: string;
    defaultValue: string;
  }[];
}

interface State {
  prescriptionId: number;
  volumingAdditionalOarInfo: string;
  volumingAdditionalOarInfoId: number;
  volumingSelectedTemplate: SelectOptionType | undefined;
  volumingSelectedTemplateId: number;
  volumingOarId: number;
}

const ROPatientVolumingPage = (): JSX.Element => {
  const tenantAppConfig = useTenantAppConfig();
  const showSpecialPhysicsConsultPage = tenantAppConfig.SpecialPhysicsConsultVisible;

  // const client = useApolloClient();

  // Non-react hooks
  const match = useRouteMatch<ROPatientCarePlanRoute>();
  const history = useHistory();
  const { id: patientId, careplanId, prescriptionIdx } = match.params;

  // React state variables
  const [state, setState] = useState<State>({
    prescriptionId: -1,
    volumingAdditionalOarInfo: '',
    volumingAdditionalOarInfoId: -1,
    volumingSelectedTemplate: undefined,
    volumingSelectedTemplateId: -1,
    volumingOarId: -1,
  });

  const [showModal, setShowModal] = useState(false);

  const { data: careplanData, loading: careplanLoading } = useQuery(GET_CAREPLAN_STATUS, {
    variables: { id: careplanId },
  });
  const cpotTriggered = useMemo(() => careplanData?.careplan?.careplanStatus === 'CPOT', [careplanData]);
  const parentSubmittedAt = useMemo(() => careplanData?.careplan?.parent?.submittedAt, [careplanData]);
  const { data: tumourStreamData } = useQuery(GET_TUMOUR_STREAM_FROM_CAREPLAN, {
    variables: { careplanId },
    onCompleted: (data: any): void => {
      logMissingDiagnosisCode(logger, data.careplan, 'Voluming');
    },
  });
  const tumourStream = useMemo(
    () => tumourStreamData?.careplan?.diagnosis?.diagnosisCode?.tumourStream?.name,
    [tumourStreamData],
  );

  const { data: careplanFiltersData } = useQuery(LIST_CAREPLAN_FILTERS, {
    variables: { patientId, tumourStream: tumourStream, hasOar: true },
    skip: !tumourStream,
  });
  const templateList = useMemo(() => {
    return careplanFiltersData?.careplanTemplateList
      .filter((templateItem: careplanTemplateList) => !!templateItem)
      .map((templateItem: careplanTemplateList) => {
        return { label: templateItem?.name, value: templateItem?.id };
      });
  }, [careplanFiltersData]);

  const [forceRefresh, setForceRefresh] = useState(true);
  const [groupVariation, setGroupVariation] = useState<boolean>(false);
  const [siteVariation, setSiteVariation] = useState<boolean>(false);

  const { loading, data, refetch } = useQuery(GET_CAREPLAN_PRESCRIPTIONS, {
    variables: { careplanId },
    fetchPolicy: 'cache-and-network',
  });

  const currentPrescriptionId: number = useMemo(() => {
    let prescriptionId = prescriptionIdx ? parseInt(prescriptionIdx) : 0;
    if (data && prescriptionIdx === 'last') {
      prescriptionId = getPrescriptionsWithSites(data.prescriptionsByCareplan).length - 1;
    }

    return prescriptionId;
  }, [data, prescriptionIdx]);

  const { data: careplanStatusData } = useQuery(GET_CAREPLAN, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: { id: careplanId },
  });
  const { data: volumingVariation, refetch: refetchVolumingVariation } = useQuery(VOLUMING_TEMPLATE_VALUE_VARIATION, {
    fetchPolicy: 'network-only',
    variables: {
      prescriptionId: String(getPrescriptionsWithSites(data?.prescriptionsByCareplan)[currentPrescriptionId]?.id || 0),
    },
    skip: loading || !data || cpotTriggered,
  });
  // Check CTV and PTV
  const { data: siteVariationBool, refetch: refetchSiteVariation } = useQuery(
    SITE_TEMPLATE_VALUES_BY_PRESCRIPTION_VARIATION,
    {
      fetchPolicy: 'network-only',
      variables: {
        prescriptionId: String(
          getPrescriptionsWithSites(data?.prescriptionsByCareplan)[currentPrescriptionId]?.id || 0,
        ),
        volumingPage: true,
      },
      skip: loading || !data || cpotTriggered,
    },
  );
  const { data: tenantConfigData } = useQuery(TENANT_CONFIG_QUERY);

  const { isMRLApplicable, loading: careplanDataContextLoading } = useCareplanDataContext();

  const targetValueDoseSuffixEnabled = useMemo(() => {
    return (
      tenantConfigData?.tenantConfig?.targetValueDoseSuffixEnabled ??
      CurrentAppConfig.VolumingPage.TargetValueDoseSuffixEnabled
    );
  }, [tenantConfigData]);

  const [cancelCpot] = useMutation(TRIGGER_CPOT, {
    awaitRefetchQueries: true,
    variables: {
      id: careplanId,
      careplanStatus: 'Prescription',
      cpotTriggered: false,
    },
    refetchQueries: [
      { query: GET_CAREPLAN_SITE_GROUPS, variables: { careplanId } },
      { query: GET_CAREPLAN, variables: { id: careplanId } },
    ],
    onCompleted: () => {
      history.push(`/radiation/patient/${patientId}/careplan/${careplanId}/${CAREPLAN_PAGES.SUBMISSION}`);
    },
  });

  const [updateManualOars] = useMutation(UPDATE_MANUAL_OARS, {
    refetchQueries: [
      { query: ORGAN_AT_RISK_TEMPLATE_VALUE_VARIATION, variables: { prescriptionId: String(state.prescriptionId) } },
      { query: OAR_QUERY, variables: { prescriptionId: String(state.prescriptionId) } },
      { query: GET_CAREPLAN_PRESCRIPTIONS, variables: { careplanId } },
      { query: VOLUMING_OAR_QUERY, variables: { prescriptionId: String(state.prescriptionId) } },
    ],
    awaitRefetchQueries: true,
  });
  const [updateVolumingValue] = useMutation(UPDATE_VOLUMING_VALUE, {
    refetchQueries: [
      { query: VOLUMING_TEMPLATE_VALUE_VARIATION, variables: { prescriptionId: String(state.prescriptionId) } },
      { query: GET_CAREPLAN, variables: { id: careplanId } },
      { query: ORGAN_AT_RISK_TEMPLATE_VALUE_VARIATION, variables: { prescriptionId: String(state.prescriptionId) } },
      { query: OAR_QUERY, variables: { prescriptionId: String(state.prescriptionId) } },
      { query: VOLUMING_OAR_QUERY, variables: { prescriptionId: String(state.prescriptionId) } },
      {
        query: SITE_TEMPLATE_VALUES_BY_PRESCRIPTION_VARIATION,
        variables: { prescriptionId: String(state.prescriptionId), volumingPage: true },
      },
    ],
  });

  const [resetVolumingData] = useMutation(RESET_VOLUMING_DATA, {
    awaitRefetchQueries: true,
    refetchQueries: [
      { query: GET_VOLUMING_OAR_DATA, variables: { prescriptionId: String(state.prescriptionId) } },
      { query: OAR_QUERY, variables: { prescriptionId: String(state.prescriptionId) } },
      { query: GET_CAREPLAN_PRESCRIPTIONS, variables: { careplanId } },
      { query: VOLUMING_OAR_QUERY, variables: { prescriptionId: String(state.prescriptionId) } },
      { query: ORGAN_AT_RISK_TEMPLATE_VALUE_VARIATION, variables: { prescriptionId: String(state.prescriptionId) } },
      { query: GET_CAREPLAN, variables: { id: careplanId } },
    ],
    onCompleted: (): void => {
      setForceRefresh(!forceRefresh);
      refetchSiteVariation();
      refetchVolumingVariation();
    },
  });

  const prescriptionTemplate = useMemo(
    () => getPrescriptionsWithSites(data?.prescriptionsByCareplan)[currentPrescriptionId]?.template,
    [data, currentPrescriptionId],
  );
  const isManual = useMemo(
    () => !prescriptionTemplate || Number(prescriptionTemplate?.id) === -1,
    [prescriptionTemplate],
  );

  useEffect((): void => {
    refetch();
  }, []);

  useEffect((): void => {
    if (data) {
      const prescriptionsWithSites = getPrescriptionsWithSites(data.prescriptionsByCareplan);
      const prescriptionId = prescriptionsWithSites[currentPrescriptionId]?.id;
      // Return if prescriptionId is not found
      if (!prescriptionId) return;
      const additionalOarInfo = prescriptionsWithSites[currentPrescriptionId].volumingvaluesSet.filter(
        (sgValue: any): boolean => sgValue.field.name === ADDITIONAL_OAR_INFO,
      );
      const selectedTemplate = prescriptionsWithSites[currentPrescriptionId].volumingvaluesSet.filter(
        (sgValue: any): boolean => sgValue.field.name === SELECTED_TEMPLATE,
      );
      const volumingSelectedTemplate = templateList?.find(
        (template: SelectOptionType) => String(template?.value) === String(selectedTemplate?.[0]?.value),
      );
      const oar = prescriptionsWithSites[currentPrescriptionId].volumingvaluesSet.filter(
        (sgValue: any): boolean => sgValue.field.name === OAR,
      );
      setState({
        ...state,
        prescriptionId: Number(prescriptionId),
        volumingAdditionalOarInfo:
          additionalOarInfo && additionalOarInfo.length > 0 ? JSON.parse(additionalOarInfo[0].value) : '',
        volumingSelectedTemplate: volumingSelectedTemplate || undefined,
        volumingAdditionalOarInfoId:
          additionalOarInfo && additionalOarInfo.length > 0 ? Number(additionalOarInfo[0].id) : -1,
        volumingSelectedTemplateId:
          selectedTemplate && selectedTemplate.length > 0 ? Number(selectedTemplate[0].id) : -1,
        volumingOarId: oar && oar.length > 0 ? Number(oar[0].id) : -1,
      });
    }
  }, [data, currentPrescriptionId, templateList]);

  const { data: oarData, loading: oarLoading } = useQuery(VOLUMING_OAR_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: { prescriptionId: String(state.prescriptionId) },
    skip: !(Number.isInteger(state.prescriptionId) && state.prescriptionId > 0),
    notifyOnNetworkStatusChange: true,
  });

  const totalOARs = Array.isArray(oarData?.roOrganAtRisk) ? oarData.roOrganAtRisk.length : 0;
  const hasNoOars = !oarLoading && totalOARs === 0;

  const showManualOARSelector = isManual || (tenantConfigData?.tenantConfig?.showOarTemplateWhenNoOars && hasNoOars);

  const prescription = useMemo(
    () => data && data.prescriptionsByCareplan[currentPrescriptionId],
    [data, currentPrescriptionId],
  );
  const siteNames = useMemo(
    () =>
      prescription && prescription.sitegroupSet
        ? prescription.sitegroupSet
            .map((siteGroup: any): string => siteGroup?.siteSet)
            .flat()
            .sort((a: any, b: any) => a.orderRanking - b.orderRanking)
            .map((site: any): string => {
              const location = site.sitevaluesSet.reduce(
                (loc: string, value: any): string => (value.field.name === 'Location' ? JSON.parse(value.value) : loc),
                '',
              );
              const laterality = site.sitevaluesSet.reduce(
                (loc: string, value: any): string =>
                  value.field.name === 'Laterality' ? JSON.parse(value.value) : loc,
                '',
              );
              return getTreatmentSiteHeader({ treatmentSite: site.treatmentSite.treatmentSite, location, laterality });
            })
            .join(', ')
        : '',
    [prescription],
  );

  useEffect(() => {
    if (volumingVariation && !cpotTriggered) {
      const variation = volumingVariation ? volumingVariation.volumingTemplateValueVariation.variation : false;
      if (!groupVariation === variation) setGroupVariation(variation);
    }

    if (siteVariationBool && !cpotTriggered) {
      const variation = siteVariationBool.siteTemplateValuesByGroupVariation?.variation
        ? siteVariationBool.siteTemplateValuesByGroupVariation?.variation
        : false;
      if (!siteVariation === variation) setSiteVariation(variation);
    }
  }, [
    volumingVariation,
    cpotTriggered,
    groupVariation,
    setGroupVariation,
    siteVariationBool,
    setSiteVariation,
    siteVariation,
  ]);

  const handleDiscardChangesUpdate = (): void => {
    refetch();
    setForceRefresh(!forceRefresh);
  };

  if (loading || careplanLoading)
    return <LoadingSpinner loadingText={'Loading Voluming'} subtitle={'Please wait while we set things up for you'} />;

  return (
    <Fragment>
      <div className="main-container-parent-wrapper">
        <div className="main-container-wrapper">
          <CareplanBanners
            data={careplanStatusData}
            variation={groupVariation || siteVariation}
            forceUpdate={handleDiscardChangesUpdate}
            refetchQueriesList={[
              { query: GET_CAREPLAN, variables: { id: careplanId } },
              { query: GET_CAREPLAN_STATUS, variables: { id: careplanId } },
              { query: GET_CAREPLAN_PRESCRIPTIONS, variables: { careplanId } },
              { query: GET_TUMOUR_STREAM_FROM_CAREPLAN, variables: { careplanId } },
            ]}
          />
          <CareplanContainer className={'ro-voluming-page'}>
            <div className="page-header">
              <Typography variant="h5">Voluming: {siteNames}</Typography>
            </div>
            <Box component="form" id="volumingForm" noValidate>
              <SiteTargetValues
                refresh={forceRefresh}
                cpotTriggered={cpotTriggered}
                targetValueDoseSuffixEnabled={targetValueDoseSuffixEnabled}
              />
              <Divider sx={{ margin: '16px 0' }} />
              <Typography paddingTop={1} paddingBottom={2} variant="h6">
                Organs At Risk
              </Typography>
            </Box>
            {prescription && showManualOARSelector && (
              <ROAutocomplete
                id="volumingSelectedTemplate"
                fieldlabel="Select OARs from Template"
                options={templateList || []}
                value={state.volumingSelectedTemplate}
                disableClearable
                onChange={async (option: SelectOptionType | string): Promise<void> => {
                  const value = typeof option === 'string' ? { label: option, value: option } : option;
                  setState({ ...state, volumingSelectedTemplate: value });

                  // Ensure prescriptionId is valid before making mutations
                  if (state.prescriptionId && state.prescriptionId !== -1) {
                    try {
                      // First update the voluming value
                      await updateVolumingValue({
                        variables: {
                          prescriptionId: String(state.prescriptionId),
                          id: state.volumingSelectedTemplateId,
                          fieldName: SELECTED_TEMPLATE,
                          value: value?.value,
                        },
                      });

                      // Then update the manual OARs
                      await updateManualOars({
                        variables: {
                          prescriptionId: String(state.prescriptionId),
                          templateId: value?.value,
                        },
                      });
                    } catch (error) {
                      // Error handling for OAR updates
                    }
                  }
                }}
              />
            )}
            {prescription &&
              (CurrentAppConfig.VolumingPage.UsePrioritizedOARs ? (
                <PrioritisedOrgansAtRiskModule
                  prescriptionId={prescription.id}
                  careplanId={careplanId}
                  cpotTriggered={cpotTriggered}
                />
              ) : (
                <OrgansAtRiskModule
                  prescriptionId={prescription.id}
                  careplanId={careplanId}
                  cpotTriggered={cpotTriggered}
                />
              ))}
            <Divider sx={{ margin: '24px 0' }} />
            <Box component="form" id="volumingForm-2" noValidate sx={{ marginTop: '8px' }}>
              <ROTextField
                id="volumingAdditionalOarInfo"
                fieldlabel={'Additional OAR Information'}
                value={state.volumingAdditionalOarInfo}
                onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                  setState({ ...state, volumingAdditionalOarInfo: removeUnicodeQuotes(event.target.value) });
                }}
                onBlur={(): void => {
                  updateVolumingValue({
                    variables: {
                      prescriptionId: String(state.prescriptionId),
                      id: state.volumingAdditionalOarInfoId,
                      fieldName: ADDITIONAL_OAR_INFO,
                      value: JSON.stringify(state.volumingAdditionalOarInfo),
                    },
                  });
                }}
                multiline
                maxRows={4}
                maxLength={-1}
              />
            </Box>
          </CareplanContainer>
          <ROPatientCarePlanPageFooter
            onReset={(): void => {
              if (cpotTriggered) {
                setShowModal(true);
              } else {
                resetVolumingData({ variables: { prescriptionId: String(state.prescriptionId) } });
              }
            }}
            resetText={cpotTriggered ? 'Discard changes' : 'Reset to templated values'}
            onBack={(): void => {
              const index = currentPrescriptionId - 1;
              if (index === -1) {
                const presIndex = getPrescriptionsWithSites(data.prescriptionsByCareplan)
                  ? getPrescriptionsWithSites(data.prescriptionsByCareplan)
                      .map((prescription: any): any =>
                        prescription.sitegroupSet.map((siteGroup: any): string => siteGroup?.siteSet).flat(),
                      )
                      .flat().length - 1
                  : 0;
                history.push(
                  `/radiation/patient/${patientId}/careplan/${careplanId}/${CAREPLAN_PAGES.PRESCRIPTION}/${presIndex}`,
                );
              } else {
                history.push(
                  `/radiation/patient/${patientId}/careplan/${careplanId}/${CAREPLAN_PAGES.VOLUMING}/${index}`,
                );
              }
            }}
            onNext={(): void => {
              if (careplanDataContextLoading) return;

              const index = currentPrescriptionId + 1;
              if (getPrescriptionsWithSites(data.prescriptionsByCareplan).length <= index) {
                if (isMRLApplicable) {
                  history.push(
                    `/radiation/patient/${patientId}/careplan/${careplanId}/${CAREPLAN_PAGES.MRL_SAFETY_SCREENING}`,
                  );
                } else {
                  history.push(
                    `/radiation/patient/${patientId}/careplan/${careplanId}/${
                      showSpecialPhysicsConsultPage ? CAREPLAN_PAGES.SPECIAL_PHYSICS_CONSULT : CAREPLAN_PAGES.SUBMISSION
                    }`,
                  );
                }
              } else {
                history.push(
                  `/radiation/patient/${patientId}/careplan/${careplanId}/${CAREPLAN_PAGES.VOLUMING}/${index}`,
                );
              }
            }}
            nextDisabled={loading && careplanDataContextLoading}
            backDisabled={loading}
          />
        </div>
        <ROPatientCarePathSidePanel />
        <ModalCpotCancel
          isOpen={showModal}
          submitFunction={() => cancelCpot()}
          dismissFunction={() => setShowModal(false)}
          modalType="cancel"
          submittedAt={parentSubmittedAt}
        />
      </div>
    </Fragment>
  );
};

export default ROPatientVolumingPage;
